import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { curatedPlaylists } from '../../mocks/playlists';

export default function PlaylistsPage() {
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const filteredPlaylists =
    activeFilter === 'all'
      ? curatedPlaylists
      : curatedPlaylists.filter((p) => p.category.toLowerCase() === activeFilter);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-32 pb-20 bg-gradient-to-br from-navy via-navy-dark to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-display font-bold text-5xl md:text-6xl mb-6">
              Curated Playlists
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Perfect soundtracks for every training session and recovery moment
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            {[
              { id: 'all', label: 'All Playlists', icon: 'ri-music-line' },
              { id: 'lifting', label: 'Lifting', icon: 'ri-fitness-line' },
              { id: 'running', label: 'Running', icon: 'ri-run-line' },
              { id: 'relaxing', label: 'Relaxing', icon: 'ri-moon-line' },
              { id: 'r&b', label: 'R&B', icon: 'ri-heart-line' },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                  activeFilter === filter.id
                    ? 'bg-electric-blue text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={filter.icon}></i>
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-baby-blue/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPlaylists.map((playlist, index) => (
              <motion.div
                key={playlist.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group cursor-pointer"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={playlist.image}
                    alt={playlist.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-electric-blue text-white text-xs font-semibold rounded-full">
                        {playlist.category}
                      </span>
                    </div>
                  </div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 flex items-center justify-center bg-electric-blue rounded-full">
                      <i className="ri-play-fill text-3xl text-white"></i>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="font-display font-bold text-xl text-navy mb-2">{playlist.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">{playlist.description}</p>

                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <i className="ri-music-2-line"></i>
                      <span>{playlist.tracks} tracks</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      <span>{playlist.duration}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {playlist.genres.map((genre, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-baby-blue/30 text-navy text-xs rounded-full"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-navy to-navy-dark text-white rounded-2xl p-12 text-center"
          >
            <div className="w-16 h-16 flex items-center justify-center bg-electric-blue rounded-full mx-auto mb-6">
              <i className="ri-spotify-line text-3xl text-white"></i>
            </div>
            <h2 className="font-display font-bold text-3xl md:text-4xl mb-4">
              Connect Your Spotify
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Sync these curated playlists directly to your Spotify account for seamless listening
            </p>
            <button className="px-8 py-4 bg-electric-blue text-white rounded-lg text-base font-semibold hover:bg-blue-500 transition-colors whitespace-nowrap cursor-pointer">
              Connect Spotify Account
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}